import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@shared/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@shared/components/ui/dropdown-menu";
import {
  EyeIcon,
  MoreHorizontal,
  ClipboardCopy,
  UserCheck,
  UserX,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { useUpdateUser } from "./useUsers";
import { useTranslation } from "react-i18next";
import { useAuth } from "@features/auth/contexts/AuthContext";
import AssignClientsDialog from "./components/AssignClientsDialog";

const UserActions = React.memo(({ user }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const { isLoading, updateUser } = useUpdateUser();
  const [showAssignClientsDialog, setShowAssignClientsDialog] =
    React.useState(false);

  const handleCopyUserId = () => {
    navigator.clipboard
      .writeText(user._id)
      .then(() => toast.success(t("messages.userIdCopied")))
      .catch(() => toast.error(t("messages.failedToCopyUserId")));
  };

  // Only show "Assign Clients" for client support users, and only to admins/managers
  const canAssignClients =
    user.role === "clientSupport" && (auth.isAdmin || auth.isManager);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="h-8 w-8 p-0"
            aria-label="Open user actions menu"
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>{t("action.actions")}</DropdownMenuLabel>
          <DropdownMenuItem onClick={handleCopyUserId}>
            <ClipboardCopy className="mr-2 h-4 w-4" />
            {t("action.copy", {
              entity: t("user.id"),
            })}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => navigate(`/users/${user._id}`)}>
            <EyeIcon className="mr-2 h-4 w-4" />
            {t("action.view", { type: "" })}
          </DropdownMenuItem>
          {canAssignClients && (
            <>
              <DropdownMenuItem
                onClick={() => setShowAssignClientsDialog(true)}
              >
                <Users className="mr-2 h-4 w-4" />
                {t("Assign Clients")}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}

          <DropdownMenuItem
            onClick={() =>
              updateUser(
                { id: user._id, values: { isActive: !user.isActive } },
                {
                  onSuccess: () => {
                    toast.success(t("messages.userUpdated"));
                  },
                  onError: (error) => {
                    toast.error(`${error.response.data.message}`);
                  },
                }
              )
            }
            disabled={isLoading}
          >
            {user.isActive ? (
              <>
                <UserX className="mr-2 h-4 w-4" />
                {t("action.deactivate", { entity: t("") })}
              </>
            ) : (
              <>
                <UserCheck className="mr-2 h-4 w-4" />
                {t("action.activate", { entity: t("") })}
              </>
            )}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {canAssignClients && (
        <AssignClientsDialog
          supportUser={user}
          open={showAssignClientsDialog}
          onOpenChange={setShowAssignClientsDialog}
        />
      )}
    </>
  );
});

UserActions.displayName = "UserActions";

export default UserActions;
